import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  TrendingUp,
  Activity,
  Zap,
  RefreshCw,
} from 'lucide-react';
import { formatDistance } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../services/api';

interface WorkerHealth {
  workerName: string;
  totalBatches: number;
  totalProcessed: number;
  totalSuccess: number;
  totalFailed: number;
  totalSkipped: number;
  successRate: number;
  avgProcessingMs: number;
  totalApiCalls: number;
  totalApiErrors: number;
  lastRun: string;
  timeSinceLastRun: any;
}

interface QueueStatus {
  platform: string;
  enrichmentStatus: string;
  count: number;
  oldestEvent: string;
  newestEvent: string;
  oldestAge: any;
}

interface Alert {
  workerName: string;
  alertType: string;
  alertMessage: string;
  timeSinceLastRun: any;
}

const EnrichmentMonitor: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [health, setHealth] = useState<WorkerHealth[]>([]);
  const [queue, setQueue] = useState<QueueStatus[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  useEffect(() => {
    checkAuth();
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  async function checkAuth() {
    try {
      const authCheck = await authAPI.checkAdminAuth();
      if (!authCheck.authenticated) {
        navigate('/admin');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      navigate('/admin');
    }
  }

  async function fetchData() {
    try {
      const [healthRes, queueRes, alertsRes] = await Promise.all([
        fetch('/api/admin/enrichment/health', { credentials: 'include' }),
        fetch('/api/admin/enrichment/queue', { credentials: 'include' }),
        fetch('/api/admin/enrichment/alerts', { credentials: 'include' }),
      ]);

      if (!healthRes.ok || !queueRes.ok || !alertsRes.ok) {
        throw new Error('Failed to fetch enrichment data');
      }

      const healthData = await healthRes.json();
      const queueData = await queueRes.json();
      const alertsData = await alertsRes.json();

      setHealth(healthData);
      setQueue(queueData);
      setAlerts(alertsData);
      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  const getSuccessRateColor = (rate: number): string => {
    if (rate >= 80) return 'text-green-600 bg-green-100';
    if (rate >= 50) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getWorkerStatusIcon = (worker: WorkerHealth) => {
    const minutesSinceLastRun = worker.timeSinceLastRun
      ? Math.floor(worker.timeSinceLastRun.seconds / 60)
      : 0;

    if (minutesSinceLastRun > 15) {
      return <XCircle className="w-5 h-5 text-red-600" />;
    }
    if (worker.successRate < 50) {
      return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
    }
    return <CheckCircle className="w-5 h-5 text-green-600" />;
  };

  const formatDuration = (interval: any): string => {
    if (!interval || !interval.seconds) return 'N/A';
    const totalSeconds = interval.seconds;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-xl text-gray-600 dark:text-gray-400 flex items-center gap-3">
            <RefreshCw className="w-6 h-6 animate-spin" />
            {t('common.loading')}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded relative">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  // Calculate summary statistics
  const totalProcessed = health.reduce((sum, w) => sum + w.totalProcessed, 0);
  const totalSuccess = health.reduce((sum, w) => sum + w.totalSuccess, 0);
  const totalFailed = health.reduce((sum, w) => sum + w.totalFailed, 0);
  const totalApiCalls = health.reduce((sum, w) => sum + w.totalApiCalls, 0);
  const overallSuccessRate = totalProcessed > 0 ? (totalSuccess / totalProcessed) * 100 : 0;

  const pendingCount = queue
    .filter(q => q.enrichmentStatus === 'pending')
    .reduce((sum, q) => sum + q.count, 0);
  const failedCount = queue
    .filter(q => q.enrichmentStatus === 'failed')
    .reduce((sum, q) => sum + q.count, 0);

  return (
    <div className="min-h-screen">

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">{t('enrichmentMonitor.title')}</h2>
          <div className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            {t('systemMonitor.lastUpdate')}: {formatDistance(lastUpdated, new Date(), { addSuffix: true })}
          </div>
        </div>

        {/* Alerts */}
        {alerts.length > 0 && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-600 p-4 mb-6">
            <div className="flex items-center mb-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
              <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-300">
                {t('enrichmentMonitor.alerts')} ({alerts.length})
              </h3>
            </div>
            {alerts.map((alert, idx) => (
              <div key={idx} className="text-yellow-700 ml-7">
                <strong>{alert.workerName}:</strong> {alert.alertMessage}
              </div>
            ))}
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-warm-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 dark:text-gray-400 text-sm">{t('enrichmentMonitor.totalProcessed')} (24h)</span>
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-3xl font-bold text-gray-800 dark:text-gray-100">{totalProcessed.toLocaleString()}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              成功 {totalSuccess.toLocaleString()} / 失敗 {totalFailed.toLocaleString()}
            </div>
          </div>

          <div className="bg-white dark:bg-warm-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 dark:text-gray-400 text-sm">{t('enrichmentMonitor.successRate')} (24h)</span>
              <Activity className="w-5 h-5 text-green-600" />
            </div>
            <div className="text-3xl font-bold text-gray-800 dark:text-gray-100">{overallSuccessRate.toFixed(1)}%</div>
            <div className="w-full bg-gray-200 dark:bg-warm-gray-700 rounded-full h-2 mt-2">
              <div
                className={`h-2 rounded-full ${overallSuccessRate >= 80 ? 'bg-green-600' : 'bg-yellow-600'}`}
                style={{ width: `${overallSuccessRate}%` }}
              />
            </div>
          </div>

          <div className="bg-white dark:bg-warm-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 dark:text-gray-400 text-sm">保留中</span>
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div className="text-3xl font-bold text-gray-800 dark:text-gray-100">{pendingCount.toLocaleString()}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              失敗 {failedCount.toLocaleString()}
            </div>
          </div>

          <div className="bg-white dark:bg-warm-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 dark:text-gray-400 text-sm">{t('enrichmentMonitor.apiCalls')} (24h)</span>
              <Zap className="w-5 h-5 text-purple-600" />
            </div>
            <div className="text-3xl font-bold text-gray-800 dark:text-gray-100">{totalApiCalls.toLocaleString()}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">YouTube Data API</div>
          </div>
        </div>

        {/* Worker Health Table */}
        <div className="bg-white dark:bg-warm-gray-800 rounded-lg shadow-md mb-6 overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 dark:bg-warm-gray-700 border-b border-gray-200 dark:border-warm-gray-700">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">{t('enrichmentMonitor.workerHealth')} (過去24時間)</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-warm-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    状態
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    ワーカー名
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('enrichmentMonitor.totalProcessed')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('enrichmentMonitor.successRate')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    平均時間
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('enrichmentMonitor.apiCalls')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('enrichmentMonitor.lastRun')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-warm-gray-800 divide-y divide-gray-200 dark:divide-warm-gray-700">
                {health.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                        <Activity className="w-12 h-12 mb-2 text-gray-300 dark:text-gray-600" />
                        <span>{t('enrichmentMonitor.noAlerts')}</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  health.map((worker) => (
                    <tr key={worker.workerName} className="hover:bg-gray-50 dark:hover:bg-warm-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getWorkerStatusIcon(worker)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{worker.workerName}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{worker.totalBatches} バッチ</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm text-gray-900 dark:text-gray-100">{worker.totalProcessed.toLocaleString()}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{worker.totalFailed} 失敗</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getSuccessRateColor(worker.successRate)}`}>
                          {worker.successRate.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                        {worker.avgProcessingMs.toFixed(0)} ms
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {worker.totalApiCalls > 0 ? worker.totalApiCalls.toLocaleString() : '-'}
                        </div>
                        {worker.totalApiErrors > 0 && (
                          <div className="text-xs text-red-600">{worker.totalApiErrors} エラー</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                        {worker.lastRun
                          ? formatDistance(new Date(worker.lastRun), new Date(), { addSuffix: true })
                          : t('enrichmentMonitor.noAlerts')}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Queue Status Table */}
        <div className="bg-white dark:bg-warm-gray-800 rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 dark:bg-warm-gray-700 border-b border-gray-200 dark:border-warm-gray-700">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">{t('enrichmentMonitor.queueStatus')}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-warm-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    プラットフォーム
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    状態
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    件数
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    最古イベント
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    キュー経過時間
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-warm-gray-800 divide-y divide-gray-200 dark:divide-warm-gray-700">
                {queue.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                        <CheckCircle className="w-12 h-12 mb-2 text-green-500" />
                        <span className="font-semibold">{t('enrichmentMonitor.noAlerts')}</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  queue.map((item, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-warm-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {item.platform}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            item.enrichmentStatus === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {item.enrichmentStatus}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                        {item.count.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                        {item.oldestEvent
                          ? formatDistance(new Date(item.oldestEvent), new Date(), { addSuffix: true })
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1 text-sm text-gray-900 dark:text-gray-100">
                          <Clock className="w-4 h-4 text-gray-400" />
                          {formatDuration(item.oldestAge)}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnrichmentMonitor;
